'use client'
import { NavItem } from '../model'
import { useMemo } from 'react'
import { SettingsLightIcon } from '@components/icons/light/SettingsLightIcon'
import { useIsAuthenticated } from '@domain/auth/Auth.context'
import get from 'lodash/get'
import { useMaintenanceSettings } from '@hooks/useMaintenanceSettings.ts'
import { NavItems } from '@constants'
import { generatePath } from '@utils/path.utils'
import { paths } from '@router'
import { supportDeskConfig } from '@config/supportDesk.config'

interface NavMenuInput {
  onTermsOfConditionsClick?: () => void
  onPrivacyPolicyClick?: () => void
}

export const useNavMenu = (props?: NavMenuInput): NavItem[] => {
  const isAuthenticated = useIsAuthenticated()
  const { data: maintenanceModeSettings } = useMaintenanceSettings()

  const adminSSOUrl = useMemo(() => {
    const authData =
      typeof window !== 'undefined' ? window.localStorage.getItem('authData') : null

    if (!authData) return ''

    const accessToken = get(JSON.parse(authData), 'tokens.access.token')
    return `${process.env.NEXT_PUBLIC_ADMIN_APP_URL}/sso?access_token=${accessToken}`
  }, [isAuthenticated])

  return useMemo(() => {
    const adminPanelLink =
      isAuthenticated && maintenanceModeSettings?.flags?.adminPanelLink
        ? [
            {
              navLinkProps: { to: adminSSOUrl },
              label: <>Admin Panel</>,
              Icon: SettingsLightIcon,
              target: '_blank',
            },
          ]
        : []

    return [
      {
        navLinkProps: { to: generatePath(paths.funds.exclusiveAccess.companies) },
        label: 'Invest',
      },
      {
        navLinkProps: { to: generatePath(paths.funds.exclusiveAccess.fundsOverview) },
        label: 'Funds',
      },
      {
        navLinkProps: { to: generatePath(paths.portfolio.main) },
        label: 'Account',
      },
      {
        navLinkProps: { to: generatePath(paths.gift) },
        label: 'Gift',
      },
      ...(isAuthenticated || supportDeskConfig.enabled
        ? [
            {
              navLinkProps: {
                to: paths.support,
              },
              label: 'Support',
            },
          ]
        : []),
      {
        navLinkProps: { to: '/blog' },
        label: 'Blog',
      },
      // SEO / GEO authority hubs — routes live; hidden from app nav (see sitemap, llms.txt)
      //   ],
      ...(props?.onTermsOfConditionsClick
        ? [
            {
              label: NavItems.TERMS_AND_CONDITIONS,
              onClick: props.onTermsOfConditionsClick,
            },
          ]
        : []),
      ...(props?.onPrivacyPolicyClick
        ? [
            {
              label: NavItems.PRIVACY_POLICY,
              onClick: props.onPrivacyPolicyClick,
            },
          ]
        : []),
      ...adminPanelLink,
    ]
  }, [isAuthenticated, maintenanceModeSettings, adminSSOUrl]) as NavItem[]
}
